import { getAllUserDevice } from 'src/@api/login'
import { switchDevice } from 'src/utils/switchDevice'
import { store } from 'src/store'
import { messageInfo } from 'src/components/Message'
import { getStaticTranslation } from 'src/utils/i18n'
import { isSelectableDevice } from 'src/utils/subscriptionStatus'

/**
 * 解析 URL search 为键值对象。
 * @param {string} search - 如 ?from_app=1&user_device_id=123
 * @returns {Object|null}
 */
export const parseSearchParams = search => {
  try {
    const query = search.match(/\?([^#]+)/)?.[1]
    if (!query) return null

    const obj = {}
    query.split('&').forEach(pair => {
      const [key, value = ''] = pair.split('=')
      if (!key) return
      obj[decodeURIComponent(key)] = decodeURIComponent(value)
    })

    return obj
  } catch {
    return null
  }
}

/**
 * 按平台类型返回对应 Dashboard 路径。
 * @param {string} platformType
 * @returns {string}
 */
export const getDashboardPathByPlatform = platformType => {
  if (platformType === 'Android-Cloud') return '/android/dashboard'
  if (platformType === 'Android-Local') return '/android/local/dashboard'

  return '/dashboard'
}

const buildDeviceUserPayload = device => ({
  username: device.iCloud_username,
  password: device.iCloud_password,
  temporary: device.temporary,
  expire_time: device.expire_time,
  likeType: device.like_type,
  device_id: device?.id || device?.device_id || '',
  order_id: device?.order_id ?? device?.orderId ?? null,
  subscription_status: device?.subscription_status ?? null
})

/**
 * 按 user_device_id 选中设备并走 switchDevice。
 * @param {string|number} userDeviceId
 * @returns {Promise<{ ok: boolean, platformType?: string, dashboardPath?: string, error?: string }>}
 */
export const applyUserDeviceById = async userDeviceId => {
  const res = await getAllUserDevice({})
  const devices = Array.isArray(res?.data) ? res.data : []
  const device = devices.find(item => String(item.id) === String(userDeviceId))

  if (!device) {
    return { ok: false, error: 'not_found' }
  }

  if (!isSelectableDevice(device)) {
    return { ok: false, error: 'lapsed_device_not_selectable' }
  }

  const deviceUser = buildDeviceUserPayload(device)

  localStorage.setItem('allDeviceUser', JSON.stringify(devices))
  localStorage.setItem('currentDevice', JSON.stringify({}))
  localStorage.setItem('Basic', device.iCloud_username ? 1 : 0)

  // 统一更新 localStorage 和切换状态；Redux 选中值保持为 device_id。
  const switched = switchDevice({
    dispatch: store.dispatch,
    deviceUser,
    currentDevice: {},
    currentMode: device.platform_type
  })

  if (!switched) {
    return { ok: false, error: 'lapsed_device_not_selectable' }
  }

  return {
    ok: true,
    platformType: device.platform_type,
    dashboardPath: getDashboardPathByPlatform(device.platform_type)
  }
}

/**
 * App 深链入站：解析 from_app / user_device_id 并切换设备。
 * @param {string} search - window.location.search
 * @returns {Promise<{ handled: boolean, ok?: boolean, error?: string }>}
 */
export const handleAppDeviceBridge = async search => {
  const params = parseSearchParams(search)
  if (!params || params.from_app !== '1') {
    return { handled: false }
  }

  // App 跳转来源平台持久化；入参可能是 iOS/Android，写入前统一小写
  if (params.app_platform) {
    sessionStorage.setItem('app_platform', String(params.app_platform).trim().toLowerCase())
  }

  const userDeviceId = params.user_device_id

  // 绑定/setup 场景只需 orderId，device id 非必须；无 user_device_id 时不提示、不处理设备桥接
  if (!userDeviceId) {
    return { handled: false }
  }

  try {
    const result = await applyUserDeviceById(userDeviceId)
    if (!result.ok) {
      const locale = localStorage.getItem('locale') || 'en'
      getStaticTranslation('Invalid device. Please try again.', locale).then(messageInfo)

      return { handled: true, ok: false, error: result.error }
    }

    const { dashboardPath } = result

    if (window.location.pathname !== dashboardPath) {
      window.location.assign(dashboardPath)

      return { handled: true, ok: true, ...result }
    }

    if (window.location.search) {
      window.history.replaceState(null, '', dashboardPath)
    }

    return { handled: true, ok: true, ...result }
  } catch {
    const locale = localStorage.getItem('locale') || 'en'
    getStaticTranslation('Network error, please try again', locale).then(messageInfo)

    return { handled: true, ok: false, error: 'request_failed' }
  }
}
